/**
 * Performance Monitoring Module
 *
 * Tracks Core Web Vitals and custom performance metrics.
 * Reports to Sentry when configured, otherwise logs to console.
 */

import { onCLS, onINP, onFCP, onLCP, onTTFB, Metric } from 'web-vitals';
import { addBreadcrumb, captureMessage, isInitialized as isSentryInitialized } from './errorTracking';

// Performance thresholds based on Google's recommendations
const THRESHOLDS = {
  CLS: { good: 0.1, needsImprovement: 0.25 },
  INP: { good: 200, needsImprovement: 500 },
  FCP: { good: 1800, needsImprovement: 3000 },
  LCP: { good: 2500, needsImprovement: 4000 },
  TTFB: { good: 800, needsImprovement: 1800 },
};

type MetricName = keyof typeof THRESHOLDS;

interface PerformanceEntry {
  name: MetricName;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  timestamp: number;
}

// Store metrics for debugging
const collectedMetrics: PerformanceEntry[] = [];

/**
 * Get rating for a metric based on thresholds
 */
function getRating(name: MetricName, value: number): 'good' | 'needs-improvement' | 'poor' {
  const threshold = THRESHOLDS[name];
  if (value <= threshold.good) return 'good';
  if (value <= threshold.needsImprovement) return 'needs-improvement';
  return 'poor';
}

/**
 * Handle metric reporting
 */
function reportMetric(metric: Metric): void {
  const { name, value, id } = metric;
  const metricName = name as MetricName;
  const rating = getRating(metricName, value);

  const entry: PerformanceEntry = {
    name: metricName,
    value,
    rating,
    timestamp: Date.now(),
  };

  collectedMetrics.push(entry);

  // Log in development
  if (import.meta.env.DEV) {
    const color = rating === 'good' ? '🟢' : rating === 'needs-improvement' ? '🟡' : '🔴';
    console.log(`${color} ${name}: ${value.toFixed(2)} (${rating})`);
  }

  // Report to Sentry if initialized
  if (isSentryInitialized()) {
    addBreadcrumb({
      category: 'web-vitals',
      message: `${name}: ${value.toFixed(2)} (${rating})`,
      level: rating === 'poor' ? 'warning' : 'info',
      data: {
        metric: name,
        value,
        rating,
        id,
      },
    });

    // Send warning for poor metrics
    if (rating === 'poor') {
      captureMessage(`Poor ${name} performance: ${value.toFixed(2)}`, 'warning');
    }
  }

  // Send to analytics endpoint if configured
  const analyticsEndpoint = import.meta.env.VITE_ANALYTICS_ENDPOINT;
  if (analyticsEndpoint) {
    sendToAnalytics(analyticsEndpoint, entry);
  }
}

/**
 * Send metric to analytics endpoint
 */
function sendToAnalytics(endpoint: string, entry: PerformanceEntry): void {
  // Use sendBeacon for reliability during page unload
  if ('sendBeacon' in navigator) {
    const data = JSON.stringify({
      type: 'web-vital',
      ...entry,
      url: window.location.href,
      userAgent: navigator.userAgent,
    });
    navigator.sendBeacon(endpoint, data);
  } else {
    // Fallback to fetch
    fetch(endpoint, {
      method: 'POST',
      body: JSON.stringify({
        type: 'web-vital',
        ...entry,
        url: window.location.href,
      }),
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
    }).catch(() => {
      // Silently fail - performance monitoring shouldn't break the app
    });
  }
}

/**
 * Initialize performance monitoring
 * Call this once on app startup
 */
export function initPerformanceMonitoring(): void {
  if (typeof window === 'undefined') return;

  try {
    // Core Web Vitals
    onCLS(reportMetric);
    onINP(reportMetric);
    onFCP(reportMetric);
    onLCP(reportMetric);
    onTTFB(reportMetric);

    if (import.meta.env.DEV) {
      console.info('Performance monitoring initialized');
    }
  } catch (error) {
    console.warn('Failed to initialize performance monitoring:', error);
  }
}

/**
 * Manually mark a performance milestone
 */
export function markPerformance(name: string): void {
  if (typeof performance === 'undefined') return;

  try {
    performance.mark(name);
  } catch {
    // Ignore errors
  }
}

/**
 * Measure time between two marks
 */
export function measurePerformance(name: string, startMark: string, endMark?: string): number | null {
  if (typeof performance === 'undefined') return null;

  try {
    const end = endMark || `${name}-end`;
    if (!endMark) {
      performance.mark(end);
    }
    performance.measure(name, startMark, end);

    const entries = performance.getEntriesByName(name, 'measure');
    const entry = entries[entries.length - 1];

    if (import.meta.env.DEV && entry) {
      console.log(`⏱️ ${name}: ${entry.duration.toFixed(2)}ms`);
    }

    return entry?.duration ?? null;
  } catch {
    return null;
  }
}

/**
 * Track map-specific performance metrics
 */
export function trackMapMetrics(): void {
  // Mark when map starts loading
  markPerformance('map-init-start');

  // Listen for map ready event
  window.addEventListener(
    'map:ready',
    () => {
      const duration = measurePerformance('map-init', 'map-init-start');
      if (duration !== null && isSentryInitialized()) {
        addBreadcrumb({
          category: 'performance',
          message: `Map initialization: ${duration.toFixed(2)}ms`,
          level: 'info',
          data: { duration },
        });
      }
    },
    { once: true }
  );
}

/**
 * Get collected metrics for debugging
 */
export function getCollectedMetrics(): PerformanceEntry[] {
  return [...collectedMetrics];
}

/**
 * Create a performance observer for specific entry types
 */
export function observePerformance(
  entryTypes: string[],
  callback: (entries: PerformanceEntryList) => void
): PerformanceObserver | null {
  if (typeof PerformanceObserver === 'undefined') return null;

  try {
    const observer = new PerformanceObserver((list) => {
      callback(list.getEntries());
    });
    observer.observe({ entryTypes });
    return observer;
  } catch {
    return null;
  }
}

/**
 * Track long tasks that may cause UI jank
 */
export function trackLongTasks(): PerformanceObserver | null {
  return observePerformance(['longtask'], (entries) => {
    entries.forEach((entry) => {
      if (import.meta.env.DEV) {
        console.warn(`🐢 Long task detected: ${entry.duration.toFixed(2)}ms`);
      }

      if (isSentryInitialized()) {
        addBreadcrumb({
          category: 'performance',
          message: `Long task: ${entry.duration.toFixed(2)}ms`,
          level: 'warning',
          data: {
            duration: entry.duration,
            startTime: entry.startTime,
          },
        });
      }
    });
  });
}
